import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, Heart, LayoutDashboard, LogOut, User, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { Logo } from '@/components/common/Logo';
import { useAuth } from '@/store/useAuth';
import { PANEL_URL } from '@/config/brand';
import { MAP_SALE, MAP_RENT } from '@/config/navLinks';
import { useFavorites } from '@/store/useFavorites';
import { listNotifications, markAllNotificationsRead, markNotificationRead } from '@/services/authClient';

const SHOW_LOKATION_LOGO = false;

const NAV_MENUS = [
  {
    label: 'Buy',
    href: MAP_SALE,
    sections: [
      {
        title: 'Homes for sale',
        links: [
          { label: 'Homes for sale', to: MAP_SALE },
          { label: 'Foreclosures', to: MAP_SALE },
          { label: 'For sale by owner', to: MAP_SALE },
          { label: 'Open houses', to: MAP_SALE },
          { label: 'New construction', to: `${MAP_SALE}&propertyCondition=New%20Construction` },
          { label: 'Coming soon', to: '/map?status=Cs&listingType=Sale' },
          { label: 'Recent home sales', to: '/map?status=Sld&listingType=Sale' },
          { label: 'All homes', to: MAP_SALE },
        ],
      },
      {
        title: 'Resources',
        links: [
          { label: 'Home Buying Guide', to: '#', comingSoon: true },
          { label: 'Foreclosure Center', to: '#', comingSoon: true },
          { label: 'Real Estate App', to: '#', comingSoon: true },
        ],
      },
    ],
  },
  {
    label: 'Sell',
    sections: [
      {
        title: 'Explore your options',
        links: [
          { label: 'Sell your home', to: '/dashboard/my-listings', requiresAuth: true },
          { label: 'Boost my listing', to: '/advertise/sellers' },
          { label: "See your home's value", to: '#', comingSoon: true },
          { label: "Seller's Guide", to: '#', comingSoon: true },
        ],
      },
      {
        title: 'Resources',
        links: [
          { label: "Find a seller's agent", to: '/agents', requiresAuth: true },
          { label: 'Pricing & tools', to: '/advertise' },
          { label: 'Home improvement', to: '#', comingSoon: true },
        ],
      },
    ],
  },
  {
    label: 'Rent',
    href: MAP_RENT,
    sections: [
      {
        title: 'Rentals',
        links: [
          { label: 'Rental buildings', to: `${MAP_RENT}&propertyType=apartments` },
          { label: 'Apartments for rent', to: `${MAP_RENT}&propertyType=apartments` },
          { label: 'Houses for rent', to: `${MAP_RENT}&propertyType=single-family` },
          { label: 'All rentals', to: MAP_RENT },
        ],
      },
      {
        title: 'Resources',
        links: [
          { label: "Renter's guide", to: '#', comingSoon: true },
          { label: 'Affordability calculator', to: '#', comingSoon: true },
          { label: 'Build credit', to: '#', comingSoon: true },
        ],
      },
    ],
  },
  {
    label: 'Agent',
    sections: [
      {
        title: 'Find an agent',
        links: [
          { label: 'Browse agents', to: '/agents', requiresAuth: true },
          { label: 'Agent directory', to: '/agents', requiresAuth: true },
          { label: 'Top-rated agents', to: '/agents', requiresAuth: true },
        ],
      },
      {
        title: 'For agents',
        links: [
          { label: 'Join as an agent', to: '/advertise/agents' },
          { label: 'Agent resources', to: '#', comingSoon: true },
          { label: 'Advertising for agents', to: '/advertise/agents' },
        ],
      },
    ],
  },
  {
    label: 'Advertise',
    sections: [
      {
        title: 'Advertise with us',
        links: [
          { label: 'Advertise overview', to: '/advertise' },
          { label: 'Agent Membership & Boost', to: '/advertise/agents' },
          { label: 'Listing Boost for sellers', to: '/advertise/sellers' },
          { label: 'Community Boost for builders', to: '/advertise/builders' },
        ],
      },
      {
        title: 'Marketing',
        links: [
          { label: 'Premium plans', to: '/dashboard/billing', requiresAuth: true },
          { label: 'Sponsored listings', to: '/advertise/agents' },
          { label: 'Contact sales', to: '/advertise/agents' },
        ],
      },
    ],
  },
];

const triggerClass =
  'rounded-full bg-transparent px-3.5 py-2 text-sm font-medium text-slate-600 hover:bg-blue-50 hover:text-blue-700 focus:bg-blue-50 focus:text-blue-700 data-[state=open]:bg-blue-50 data-[state=open]:text-blue-700 h-auto [&>svg]:hidden';

const MegaPanel = ({ sections, onNavigate }) => (
  <div className="grid w-[min(92vw,36rem)] gap-6 p-5 sm:grid-cols-2">
    {sections.map((section) => (
      <div key={section.title}>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{section.title}</p>
        <ul className="space-y-1">
          {section.links.map((link) => (
            <li key={link.label}>
              {link.comingSoon ? (
                <span className="flex items-center justify-between rounded-md px-2 py-1.5 text-sm text-slate-400">
                  {link.label}
                  <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Soon</span>
                </span>
              ) : (
                <NavigationMenuLink asChild>
                  <Link
                    to={link.to}
                    onClick={(e) => onNavigate?.(e, link)}
                    className="block rounded-md px-2 py-1.5 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700 focus:bg-blue-50 focus:text-blue-700 focus:outline-none"
                  >
                    {link.label}
                  </Link>
                </NavigationMenuLink>
              )}
            </li>
          ))}
        </ul>
      </div>
    ))}
  </div>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const getIds = useFavorites((s) => s.getIds);
  const favCount = getIds().length;

  const handleAuthNav = (e, link) => {
    if (link.requiresAuth && !isAuthenticated) {
      e.preventDefault();
      navigate('/login', { state: { from: link.to } });
    }
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      listNotifications()
        .then((data) => {
          setNotifications(data.results || []);
          setUnreadCount(data.unread_count || 0);
        })
        .catch(() => setNotifications([]));
    }
  }, [isAuthenticated]);

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const handleNotificationClick = async (n) => {
    if (!n.is_read) {
      try {
        await markNotificationRead(n.id);
        setNotifications((prev) => prev.map((item) => (item.id === n.id ? { ...item, is_read: true } : item)));
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch {}
    }
    if (n.url) navigate(n.url);
  };

  return (
    <header
      data-testid="navbar"
      className={cn(
        'sticky top-0 z-50 border-b border-slate-200 bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/70 transition-shadow',
        scrolled && 'shadow-sm'
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid h-24 grid-cols-[1fr_auto_1fr] items-center gap-2">
          <div className="hidden min-w-0 lg:block">
            <NavigationMenu className="max-w-none justify-start">
              <NavigationMenuList className="justify-start space-x-0">
                {NAV_MENUS.map((menu) => (
                  <NavigationMenuItem key={menu.label}>
                    <NavigationMenuTrigger
                      className={triggerClass}
                      data-testid={`nav-link-${menu.label.toLowerCase()}`}
                      onClick={(e) => {
                        if (!menu.href) return;
                        e.preventDefault();
                        navigate(menu.href);
                      }}
                    >
                      {menu.label}
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <MegaPanel sections={menu.sections} onNavigate={handleAuthNav} />
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                ))}
              </NavigationMenuList>
            </NavigationMenu>
          </div>
          <div className="lg:hidden" aria-hidden="true" />

          <div className="justify-self-center flex items-center gap-3">
            {SHOW_LOKATION_LOGO && (
              <a
                href="https://lokationre.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Lokation Real Estate"
                className="shrink-0"
              >
                <img
                  src="/lokation-logo.png"
                  alt="Lokation Real Estate"
                  className="h-8 sm:h-10 w-auto max-w-[160px] object-contain"
                />
              </a>
            )}
            <Logo />
          </div>

          <div className="flex items-center justify-end gap-2">
            <Link to="/dashboard/saved-homes" className="relative hidden sm:grid h-10 w-10 place-items-center rounded-full text-slate-600 hover:bg-slate-100 transition-colors" aria-label="Saved homes" data-testid="navbar-saved">
              <Heart size={20} />
              {favCount > 0 && <span className="absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-blue-600 px-1 text-[10px] font-semibold text-white">{favCount}</span>}
            </Link>

            {isAuthenticated && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="relative hidden sm:grid h-10 w-10 place-items-center rounded-full text-slate-600 hover:bg-slate-100 transition-colors" aria-label="Notifications" data-testid="navbar-notifications">
                    <Bell size={20} />
                    {unreadCount > 0 && <span className="absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-blue-600 px-1 text-[10px] font-semibold text-white">{unreadCount}</span>}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  <div className="flex items-center justify-between px-2 py-2">
                    <span className="text-sm font-semibold text-slate-900">Notifications</span>
                    {unreadCount > 0 && (
                      <button onClick={handleMarkAllRead} className="text-xs text-blue-600 hover:text-blue-700 font-medium">
                        Mark all read
                      </button>
                    )}
                  </div>
                  <DropdownMenuSeparator />
                  <ScrollArea className="h-[320px]">
                    {notifications === null ? (
                      <div className="px-2 py-8 text-center text-sm text-slate-500">Loading...</div>
                    ) : notifications.length === 0 ? (
                      <div className="px-2 py-8 text-center text-sm text-slate-500">No notifications</div>
                    ) : (
                      notifications.slice(0, 10).map((n) => (
                        <DropdownMenuItem
                          key={n.id}
                          onClick={() => handleNotificationClick(n)}
                          className={cn('flex cursor-pointer items-start gap-2 px-3 py-2.5', !n.is_read && 'bg-blue-50')}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-slate-900">{n.title}</p>
                            <p className="text-xs text-slate-600 line-clamp-2">{n.body}</p>
                            <span className="text-[10px] text-slate-400 mt-1 block">{new Date(n.created_at).toLocaleDateString()}</span>
                          </div>
                          {!n.is_read && <div className="mt-1 h-2 w-2 rounded-full bg-blue-600 shrink-0" />}
                        </DropdownMenuItem>
                      ))
                    )}
                  </ScrollArea>
                  {notifications && notifications.length > 10 && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/dashboard/notifications')} className="text-center text-sm text-blue-600 hover:text-blue-700 font-medium cursor-pointer">
                        View all notifications
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 hover:bg-slate-50 transition-colors" data-testid="navbar-user-menu">
                    <span className="text-sm font-medium text-slate-700 max-w-32 truncate">{user?.name}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem onClick={() => { window.location.href = PANEL_URL; }}><LayoutDashboard size={16} className="mr-2" />Dashboard</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/dashboard/profile')}><User size={16} className="mr-2" />Profile</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={async () => { await logout(); navigate('/'); }}><LogOut size={16} className="mr-2" />Sign out</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login" className="hidden sm:inline-flex text-sm font-medium text-slate-700 hover:text-blue-700 px-3 py-2" data-testid="navbar-signin-link">Sign In</Link>
            )}

            <Button asChild className="hidden sm:inline-flex rounded-full" data-testid="navbar-primary-cta">
              <Link
                to={isAuthenticated ? '/dashboard/billing' : '/register'}
                state={isAuthenticated ? undefined : { from: '/dashboard/billing' }}
              >
                Create Account
              </Link>
            </Button>

            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden grid h-10 w-10 place-items-center rounded-full text-slate-700 hover:bg-slate-100" aria-label="Open menu" data-testid="navbar-mobile-toggle">
                  <Menu size={22} />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80">
                <SheetHeader>
                  <SheetTitle>
                    <div className="flex items-center gap-2">
                      {SHOW_LOKATION_LOGO && (
                        <a
                          href="https://lokationre.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Lokation Real Estate"
                          className="shrink-0"
                        >
                          <img
                            src="/lokation-logo.png"
                            alt="Lokation Real Estate"
                            className="h-8 w-auto max-w-[140px] object-contain"
                          />
                        </a>
                      )}
                      <Logo />
                    </div>
                  </SheetTitle>
                </SheetHeader>
                <div className="mt-4 flex flex-col gap-1">
                  <Accordion type="single" collapsible className="w-full">
                    {NAV_MENUS.map((menu) => (
                      <AccordionItem key={menu.label} value={menu.label} className="border-slate-200">
                        {menu.href ? (
                          <div className="flex items-center">
                            <Link
                              to={menu.href}
                              onClick={() => setOpen(false)}
                              className="flex-1 px-1 py-3 text-base font-medium text-slate-700"
                              data-testid={`nav-link-${menu.label.toLowerCase()}-mobile`}
                            >
                              {menu.label}
                            </Link>
                            <AccordionTrigger
                              className="flex-none px-2 py-3 hover:no-underline"
                              aria-label={`Open ${menu.label} menu`}
                            />
                          </div>
                        ) : (
                          <AccordionTrigger className="px-1 py-3 text-base font-medium text-slate-700 hover:no-underline">
                            {menu.label}
                          </AccordionTrigger>
                        )}
                        <AccordionContent className="pb-3">
                          <div className="space-y-4 pl-1">
                            {menu.sections.map((section) => (
                              <div key={section.title}>
                                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">{section.title}</p>
                                <ul className="space-y-0.5">
                                  {section.links.map((link) => (
                                    <li key={link.label}>
                                      {link.comingSoon ? (
                                        <span className="flex items-center justify-between rounded-lg px-2 py-2 text-sm text-slate-400">
                                          {link.label}
                                          <span className="text-[10px] font-semibold uppercase tracking-wide">Soon</span>
                                        </span>
                                      ) : (
                                        <Link
                                          to={link.to}
                                          onClick={(e) => {
                                            handleAuthNav(e, link);
                                            setOpen(false);
                                          }}
                                          className="block rounded-lg px-2 py-2 text-sm text-slate-700 hover:bg-blue-50"
                                        >
                                          {link.label}
                                        </Link>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                  <div className="mt-4 flex flex-col gap-2">
                    {isAuthenticated ? (
                      <>
                        <Button variant="outline" className="rounded-full" onClick={() => { setOpen(false); window.location.href = PANEL_URL; }}>Dashboard</Button>
                        <Button onClick={async () => { await logout(); setOpen(false); navigate('/'); }} variant="ghost" className="rounded-full">Sign out</Button>
                      </>
                    ) : (
                      <Button asChild variant="outline" className="rounded-full"><Link to="/login" onClick={() => setOpen(false)}>Sign In</Link></Button>
                    )}
                    <Button asChild className="rounded-full">
                      <Link
                        to={isAuthenticated ? '/dashboard/billing' : '/register'}
                        state={isAuthenticated ? undefined : { from: '/dashboard/billing' }}
                        onClick={() => setOpen(false)}
                      >
                        Create Account
                      </Link>
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};
